/* =============================================================
   DeleteConfirmModal.jsx — Generic delete confirmation modal
   -------------------------------------------------------------
   Props:
     title      (string)  — "Delete [name]?"
     message    (string)  — main confirmation copy
     warning    (string)  — optional amber warning block (e.g. student count)
     onConfirm  (fn)      — async handler; modal shows spinner while running
     onClose    (fn)      — called on Cancel or Escape
   ============================================================= */

import { useState, useEffect } from 'react';

export default function DeleteConfirmModal({ title, message, warning, onConfirm, onClose }) {
  const [deleting, setDeleting] = useState(false);

  /* Close on Escape */
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && !deleting) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, deleting]);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget && !deleting) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <span className="material-symbols-outlined text-[18px] text-red-600">delete</span>
          </div>
          <h2 className="font-display text-base font-semibold text-[#0b1c30]">{title}</h2>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-3">
          <p className="text-sm text-slate-600 leading-relaxed">{message}</p>

          {warning && (
            <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <span className="material-symbols-outlined text-[16px] text-amber-500 flex-shrink-0 mt-0.5">warning</span>
              <p className="text-sm text-amber-800 leading-relaxed">{warning}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={deleting}
            className="px-5 py-2.5 text-sm font-medium rounded-xl transition-all flex items-center gap-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-[16px]">
              {deleting ? 'hourglass_empty' : 'delete'}
            </span>
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>

      </div>
    </div>
  );
}
